// --------------------------------------------------------------
// Created On: 2025-07-15
// Author: Dimitra Weinstein
//
// Last Modified: 2025-10-27
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { Fragment } from "react";
import styles from "../SearchPage.module.scss";
import { PRODUCT_MANUFACTURERS } from "src/constants/miscellaneous";

// Component for advanced search inputs.
// Allows users to search by asset name, nickname, product type, manufacturer, model, identifier,
// controller model, device model, and device identifier.
export default function AdvancedSearch(props: Props): Component {
  return (
    <Fragment>
      <div className={`${styles.searchInputRow} row`}>
        <div className="mb-1 col-12 col-sm-6">
          <label className="mb-1">Asset Name</label>
          <input
            data-test="search-asset-name-input"
            className={`${styles.searchInputField} form-control mx-auto`}
            type="text"
            value={props.assetName}
            onChange={(e) => props.onAssetNameChange(e.target.value)}
          />
        </div>

        <div className="mb-1 col-12 col-sm-6">
          <label className="mb-1">Asset Nickname</label>
          <input
            data-test="search-asset-nickname-input"
            className={`${styles.searchInputField} form-control mx-auto`}
            type="text"
            value={props.assetNickname}
            onChange={(e) => props.onAssetNicknameChange(e.target.value)}
          />
        </div>
      </div>

      <div className={`${styles.searchInputRow} row`}>
        <div className="mb-1 col-12 col-sm-6">
          <label className="mb-1">Product Type</label>
          <select
            data-test="search-product-type-select"
            className={`${styles.searchInputField} form-select mx-auto`}
            value={props.productType}
            onChange={(e) => props.onProductTypeChange(e.target.value)}
          >
            <option value="">Any</option>
            {props.productTypes.map((productType) => (
              <option key={productType} value={productType}>
                {productType}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-1 col-12 col-sm-6">
          <label className="mb-1">Product Manufacturer</label>
          <select
            data-test="search-product-manufacturer-select"
            className={`${styles.searchInputField} form-select mx-auto`}
            value={props.productManufacturer}
            onChange={(e) => props.onProductManufacturerChange(e.target.value)}
          >
            <option value="">Any</option>
            {PRODUCT_MANUFACTURERS.map((productManufacturer) => (
              <option key={productManufacturer.code} value={productManufacturer.code}>
                {productManufacturer.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={`${styles.searchInputRow} row`}>
        <div className="mb-1 col-12 col-sm-6">
          <label className="mb-1">Product Part Number</label>
          <input
            data-test="search-product-part-number-input"
            className={`${styles.searchInputField} form-control mx-auto`}
            type="text"
            value={props.productModel}
            onChange={(e) => props.onProductModelChange(e.target.value)}
          />
        </div>

        <div className="mb-1 col-12 col-sm-6">
          <label className="mb-1">Product Serial Number</label>
          <input
            data-test="search-product-serial-number-input"
            className={`${styles.searchInputField} form-control mx-auto`}
            type="text"
            value={props.productIdentifier}
            onChange={(e) => props.onProductIdentifierChange(e.target.value)}
          />
        </div>
      </div>

      <div className={`${styles.searchInputRow} row`}>
        <div className="mb-1 col-12 col-sm-6">
          <label className="mb-1">Monitoring Device Type</label>
          <select
            data-test="search-monitoring-device-type-select"
            className={`${styles.searchInputField} form-select mx-auto`}
            value={props.deviceModel}
            onChange={(e) => props.onDeviceModelChange(e.target.value)}
          >
            <option value="">Any</option>
            {props.deviceTypes.map((deviceType) => (
              <option key={deviceType.model} value={deviceType.model}>
                {deviceType.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-1 col-12 col-sm-6">
          <label className="mb-1">Monitoring Device Identifier</label>
          <input
            data-test="search-monitoring-device-identifier-input"
            className={`${styles.searchInputField} form-control mx-auto`}
            type="text"
            value={props.deviceIdentifier}
            onChange={(e) => props.onDeviceIdentifierChange(e.target.value)}
          />
        </div>
      </div>

      <div className={`${styles.searchInputRow} row`}>
        <div className="mb-1 col-12 col-sm-6">
          <label className="mb-1">Controller</label>
          <select
            data-test="search-controller-select"
            className={`${styles.searchInputField} form-select mx-auto`}
            value={props.controllerModel}
            onChange={(e) => props.onControllerModelChange(e.target.value)}
          >
            <option value="">Any</option>
            {props.controllers.map((controller) => (
              <option key={controller.model} value={controller.model}>
                {controller.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Fragment>
  );
}

interface Props {
  assetName: string;
  assetNickname: string;
  productType: string;
  productManufacturer: string;
  productModel: string;
  productIdentifier: string;
  controllerModel: string;
  deviceModel: string;
  deviceIdentifier: string;
  productTypes: string[];
  controllers: ControllerSelection[];
  deviceTypes: DeviceTypeSelection[];
  onAssetNameChange: (value: string) => void;
  onAssetNicknameChange: (value: string) => void;
  onProductTypeChange: (value: string) => void;
  onProductManufacturerChange: (value: string) => void;
  onProductModelChange: (value: string) => void;
  onProductIdentifierChange: (value: string) => void;
  onControllerModelChange: (value: string) => void;
  onDeviceModelChange: (value: string) => void;
  onDeviceIdentifierChange: (value: string) => void;
}

interface ControllerSelection {
  model: string;
  name: string;
}

interface DeviceTypeSelection {
  model: string;
  name: string;
}
